import { useId } from 'react';
import classNames from 'classnames';

import {
  CheckboxFilledIcon,
  CheckboxIcon,
  CheckboxPartialIcon,
  CheckMarkAltIcon,
} from '../../icons';

import styles from './_index.module.scss';

export interface CheckboxProps {
  'aria-label'?: string;
  className?: string;
  'data-testid'?: string;
  id?: string;
  isChecked?: boolean;
  isDisabled?: boolean;
  isInverseColor?: boolean;
  isPartial?: boolean;
  label?: React.ReactNode;
  onChange?: (value: any) => void;
  title?: string;
  value: any;
}

const Checkbox = ({
  'aria-label': ariaLabel,
  className,
  'data-testid': dataTestId = 'checkbox',
  id,
  isChecked,
  isDisabled,
  isInverseColor,
  isPartial,
  label,
  onChange = () => {},
  title,
  value,
}: CheckboxProps) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const handleChange = () => {
    if (!isDisabled) {
      onChange(value);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (isDisabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      handleChange();
    }
  };

  const renderCheckboxIcon = () => {
    if (isInverseColor && isChecked) return <CheckMarkAltIcon />;
    if (isChecked) return <CheckboxFilledIcon />;
    if (isPartial) return <CheckboxPartialIcon />;
    return <CheckboxIcon />;
  };

  return (
    <div
      className={classNames(styles['checkbox'], className, {
        [styles['is-checked']]: isChecked,
        [styles['is-partial']]: isPartial,
      })}
      data-testid={dataTestId}
    >
      <input
        checked={Boolean(isChecked)}
        disabled={isDisabled}
        id={inputId}
        onChange={handleChange}
        tabIndex={-1}
        type="checkbox"
      />
      <label
        className={classNames(styles['checkbox-label'], {
          [styles['is-disabled']]: isDisabled,
        })}
        htmlFor={inputId}
        title={title}
      >
        <div
          aria-checked={Boolean(isChecked)}
          aria-disabled={isDisabled || undefined}
          aria-label={ariaLabel}
          className={styles['checkbox-icon']}
          onKeyDown={handleKeyDown}
          role="checkbox"
          tabIndex={isDisabled ? -1 : 0}
        >
          {renderCheckboxIcon()}
        </div>
        {label}
      </label>
    </div>
  );
};

export default Checkbox;
